import React, { useEffect, useState, useContext } from 'react';
import { AuthContext } from '../../context/AuthContext';
import { NavigationContext } from '../../context/NavigationContext';
import { toast } from 'react-toastify';
import './Subscriptions.css';
import SubscriptionImportWizard from './SubscriptionImportWizard';

interface Subscription {
    id: number;
    description: string | null;
    annualPrice: number; // cents
    taxRate: number; // percent
    ownerRoundTableId: number | null;
    ownerRoundTable?: { id: number; name: string; number: number } | null;
    mostRecentRechnungId: number | null;
    createdAt: string;
}

const SubscriptionsComponent: React.FC = () => {
    const authContext = useContext(AuthContext);
    const navigationContext = useContext(NavigationContext);
    if (!authContext) throw new Error('SubscriptionsComponent must be used within AuthProvider');
    if (!navigationContext) throw new Error('SubscriptionsComponent must be used within NavigationProvider');
    const { token } = authContext;
    // NavigationContext currently not used (placeholder for future detail views)

    const [loading, setLoading] = useState(true);
    const [subs, setSubs] = useState<Subscription[]>([]);
    const [showImportWizard, setShowImportWizard] = useState(false);

    const loadSubscriptions = async () => {
        try {
            const res = await fetch('/api/subscriptions', {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (!res.ok) throw new Error('Fehler beim Laden der Abonnements');
            const data = await res.json();
            setSubs(data);
        } catch (e) {
            console.error(e);
            toast.error(e instanceof Error ? e.message : 'Unbekannter Fehler beim Laden');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { loadSubscriptions(); }, [token]);

    const formatPrice = (cents: number) => {
        return `${(cents / 100).toFixed(2)} €`;
    };

    const openImportWizard = () => setShowImportWizard(true);
    const closeImportWizard = () => setShowImportWizard(false);

    if (loading) {
        return <div className="subscriptions-container"><div className="subscriptions-loading">Lade Abonnements...</div></div>;
    }

    return (
        <div className="subscriptions-container">
            <div className="subscriptions-header">
                <h1>Abonnements</h1>
                <div className="subscriptions-actions">
                    <button className="import-button" onClick={openImportWizard} aria-label="Import Wizard öffnen">Import</button>
                </div>
            </div>
            {subs.length === 0 ? (
                <p className="no-data">Keine Abonnements vorhanden</p>
            ) : (
                <table className="subscriptions-table">
                    <thead>
                        <tr>
                            <th>ID</th>
                            <th>Beschreibung</th>
                            <th>Jahrespreis</th>
                            <th>Steuersatz</th>
                            <th>RTD</th>
                            <th>Neueste Rechnung</th>
                            <th>Erstellt</th>
                        </tr>
                    </thead>
                    <tbody>
                        {subs.map(s => (
                            <tr key={s.id}>
                                <td>{s.id}</td>
                                <td>{s.description || '—'}</td>
                                <td>{formatPrice(s.annualPrice)}</td>
                                <td>{s.taxRate}%</td>
                                <td>{s.ownerRoundTable ? `${s.ownerRoundTable.name} (RT ${s.ownerRoundTable.number})` : '—'}</td>
                                <td>{s.mostRecentRechnungId ? `#${s.mostRecentRechnungId}` : '—'}</td>
                                <td>{new Date(s.createdAt).toLocaleDateString()}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}
            {showImportWizard && (
                <SubscriptionImportWizard
                    modal
                    onClose={closeImportWizard}
                    onImported={(count) => { loadSubscriptions(); toast.success(`${count} Abonnements importiert`); }}
                />
            )}
        </div>
    );
};

export default SubscriptionsComponent;
